// frontend/src/pages/CamposObligatoriosPropiedad.jsx
import { useState, useEffect, useCallback } from "react";
import { useTheme } from "../components/ThemeProvider";
import api from "../services/api";
import { normalizeSearchText } from "../utils/searchText";
import usePermiso from "../hooks/usePermiso";

const ROLES = [
  { key: "agente",     label: "Agente",     color: "#059669", bg: "#d1fae5", bgDark: "#052e16" },
  { key: "secretaria", label: "Secretaria", color: "#7c3aed", bg: "#ede9fe", bgDark: "#1e0a3c" },
  { key: "director",   label: "Director",   color: "#92400e", bg: "#fef3c7", bgDark: "#271a04" },
];

const GRUPO_COLORES = {
  "Principales":  "#2563eb",
  "Generales":    "#059669",
  "Distribución": "#7c3aed",
  "Equipamiento": "#db2777",
  "Ubicación":    "#0891b2",
  "Económicos":   "#d97706",
  "Dirección":    "#ea580c",
  "Multimedia":   "#0284c7",
  "Propietarios": "#475569",
  "Internos":     "#4f46e5",
  "Resumen":      "#334155",
  "Firma":        "#0f766e",
};

export default function CamposObligatoriosPropiedad() {
  const { theme } = useTheme();
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const veToggle = puede("cfg_obl_toggle", "ver");
  const modToggle = puede("cfg_obl_toggle", "modificar");
  const veResponsable = puede("cfg_obl_responsable", "ver");
  const modResponsable = puede("cfg_obl_responsable", "modificar");
  const vePorTipo = puede("cfg_obl_por_tipo", "ver");
  const modPorTipo = puede("cfg_obl_por_tipo", "modificar");
  const veFiltros = puede("cfg_obl_filtros", "ver");
  const puedeGuardarAlgo = modToggle || modResponsable || modPorTipo;

  const [campos,    setCampos]    = useState([]);
  const [tiposInmueble, setTiposInmueble] = useState([]);
  const [cargando,  setCargando]  = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [guardado,  setGuardado]  = useState(false);
  const [cambios,   setCambios]   = useState(false);
  const [error,     setError]     = useState("");
  const [busqueda,  setBusqueda]  = useState("");
  const [filtroGrupo, setFiltroGrupo] = useState("todos");
  const [soloObligatorios, setSoloObligatorios] = useState(false);
  const [mostrarTipos, setMostrarTipos] = useState(false);

  // ── Colores tema ──────────────────────────────────────────────────────────
  const bgPage  = isDark ? "#0a0c12"  : "#f1f5f9";
  const bgPanel = isDark ? "#0f1117"  : "#ffffff";
  const bgCard  = isDark ? "#12151e"  : "#ffffff";
  const bgHead  = isDark ? "#0d1018"  : "#f8fafc";
  const txtMain = isDark ? "#f1f5f9"  : "#1e293b";
  const txtMuted= isDark ? "#94a3b8"  : "#64748b";
  const border  = isDark ? "#252d42"  : "#e2e8f0";
  const bgInput = isDark ? "#161b28"  : "#f8fafc";

  // ── Carga ────────────────────────────────────────────────────────────────
  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    setCargando(true);
    setError("");
    try {
      const [camposRes, tiposRes] = await Promise.all([
        api.get("/campos-obligatorios"),
        api.get("/auxiliares/tipo-inmueble"),
      ]);
      setCampos(camposRes.data.data || []);
      setTiposInmueble(tiposRes.data?.data || []);
      setCambios(false);
    } catch (err) {
      setError("Error al cargar: " + (err.response?.data?.error || err.message));
    } finally {
      setCargando(false);
    }
  };

  // ── Mutaciones locales ───────────────────────────────────────────────────
  const toggleObligatorio = useCallback((id) => {
    setCampos(prev => prev.map(c =>
      c.id === id ? { ...c, obligatorio: !c.obligatorio } : c
    ));
    setCambios(true);
    setGuardado(false);
  }, []);

  const toggleResponsable = useCallback((id, rol) => {
    setCampos(prev => prev.map(c => {
      if (c.id !== id) return c;
      const actual = c.responsable || [];
      const nuevo  = actual.includes(rol)
        ? actual.filter(r => r !== rol)
        : [...actual, rol];
      return { ...c, responsable: nuevo };
    }));
    setCambios(true);
    setGuardado(false);
  }, []);

  const toggleTipoInmueble = useCallback((id, tipoId) => {
    setCampos(prev => prev.map(c => {
      if (c.id !== id) return c;
      const actual = c.tipos_inmueble || [];
      const nuevo = actual.includes(tipoId)
        ? actual.filter(t => t !== tipoId)
        : [...actual, tipoId];
      return { ...c, tipos_inmueble: nuevo };
    }));
    setCambios(true);
    setGuardado(false);
  }, []);

  // ── Guardar ──────────────────────────────────────────────────────────────
  const guardar = async () => {
    if (!puedeGuardarAlgo) return;
    setGuardando(true);
    setError("");
    try {
      await api.post("/campos-obligatorios/bulk", {
        campos: campos.map(c => ({
          id:             c.id,
          obligatorio:    c.obligatorio ?? false,
          responsable:    c.responsable ?? [],
          tipos_inmueble: c.tipos_inmueble ?? [],
        })),
      });
      setCambios(false);
      setGuardado(true);
      setTimeout(() => setGuardado(false), 2500);
    } catch (err) {
      setError("Error al guardar: " + (err.response?.data?.error || err.message));
    } finally {
      setGuardando(false);
    }
  };

  // ── Datos derivados ──────────────────────────────────────────────────────
  const grupos = ["todos", ...Array.from(new Set(campos.map(c => c.campo_grupo).filter(Boolean)))];

  const camposFiltrados = campos.filter(c => {
    if (busqueda.trim()) {
      const q = normalizeSearchText(busqueda);
      if (!normalizeSearchText(c.campo_label).includes(q) && !normalizeSearchText(c.campo_key).includes(q)) return false;
    }
    if (filtroGrupo !== "todos" && c.campo_grupo !== filtroGrupo) return false;
    if (soloObligatorios && !c.obligatorio) return false;
    return true;
  });

  // Agrupar para mostrar separadores de grupo
  const camposAgrupados = (() => {
    const mapa = new Map();
    camposFiltrados.forEach(c => {
      const g = c.campo_grupo || "Sin grupo";
      if (!mapa.has(g)) mapa.set(g, []);
      mapa.get(g).push(c);
    });
    return mapa;
  })();

  const totalObligatorios = campos.filter(c => c.obligatorio).length;

  // ── Loading ──────────────────────────────────────────────────────────────
  if (cargando) return (
    <div style={{ padding: 40, textAlign: "center", color: txtMuted, fontSize: 14, background: bgPage, minHeight: "100vh" }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
      Cargando campos...
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 16, background: bgPage, minHeight: "100vh" }}>

      {/* CABECERA */}
      <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10, alignItems: "flex-start" }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 800, color: txtMain, margin: 0 }}>
            📋 Campos obligatorios — Propiedad
          </h1>
          <p style={{ color: txtMuted, marginTop: 4, fontSize: 12, margin: "4px 0 0" }}>
            {campos.length} campos · <span style={{ color: "#dc2626", fontWeight: 700 }}>{totalObligatorios} obligatorios</span>
            {cambios && <span style={{ color: "#f59e0b", marginLeft: 12, fontWeight: 700 }}>● Cambios sin guardar</span>}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={cargar} style={{
            padding: "7px 12px", borderRadius: 8, border: `1px solid ${border}`,
            background: bgInput, color: txtMuted, fontSize: 12, cursor: "pointer",
          }}>↺ Recargar</button>
          <button onClick={guardar} disabled={guardando || !cambios || !puedeGuardarAlgo} title={!puedeGuardarAlgo ? "No tienes permiso para modificar campos obligatorios" : undefined} style={{
            padding: "8px 20px", borderRadius: 8, border: "none",
            background: guardado ? "#22c55e" : cambios ? "#f59e0b" : "#94a3b8",
            color: "#fff", fontSize: 13, fontWeight: 700,
            cursor: guardando || !cambios || !puedeGuardarAlgo ? "not-allowed" : "pointer",
            transition: "background 0.3s",
            boxShadow: cambios ? "0 2px 8px rgba(245,158,11,0.3)" : "none",
          }}>
            {guardando ? "Guardando..." : guardado ? "✓ Guardado" : "Guardar"}
          </button>
        </div>
      </div>

      {/* ERROR */}
      {error && (
        <div style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 8, background: isDark ? "#2d1515" : "#fef2f2", border: "1px solid #ef4444", color: "#ef4444", fontSize: 12 }}>
          ⚠️ {error}
        </div>
      )}

      {/* LEYENDA */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12, alignItems: "center", padding: "10px 14px", background: bgPanel, borderRadius: 10, border: `1px solid ${border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          <span style={{ width: 14, height: 14, borderRadius: 3, background: "#dc2626", display: "inline-block" }} />
          <span style={{ color: txtMain, fontWeight: 600 }}>Obligatorio</span>
          <span style={{ color: txtMuted }}>— campo que debe estar rellenado</span>
        </div>
        <div style={{ width: 1, height: 14, background: border }} />
        {ROLES.map(r => (
          <div key={r.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
            <span style={{ width: 14, height: 14, borderRadius: 3, background: r.color, display: "inline-block" }} />
            <span style={{ color: txtMain, fontWeight: 600 }}>{r.label}</span>
            <span style={{ color: txtMuted }}>— responsable de rellenarlo</span>
          </div>
        ))}
      </div>

      {/* BARRA FILTROS */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10, alignItems: "center", padding: "10px 12px", background: bgPanel, borderRadius: 10, border: `1px solid ${border}` }}>
        {veFiltros && (
        <>
        <input
          placeholder="🔍 Buscar campo..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          style={{ padding: "6px 11px", borderRadius: 7, border: `1px solid ${border}`, background: bgInput, color: txtMain, fontSize: 12, width: 180, outline: "none" }}
        />
        <select value={filtroGrupo} onChange={e => setFiltroGrupo(e.target.value)} style={{
          padding: "6px 10px", borderRadius: 7, border: `1px solid ${filtroGrupo !== "todos" ? "#2563eb" : border}`,
          background: filtroGrupo !== "todos" ? (isDark ? "#0f1e35" : "#eff6ff") : bgInput,
          color: filtroGrupo !== "todos" ? "#2563eb" : txtMain,
          fontSize: 12, cursor: "pointer", outline: "none", fontWeight: filtroGrupo !== "todos" ? 700 : 400,
        }}>
          {grupos.map(g => <option key={g} value={g}>{g === "todos" ? "Todos los grupos" : g}</option>)}
        </select>
        <button
          onClick={() => setSoloObligatorios(v => !v)}
          style={{
            padding: "6px 12px", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer",
            border: `1px solid ${soloObligatorios ? "#dc2626" : border}`,
            background: soloObligatorios ? (isDark ? "#3b0f0f" : "#fee2e2") : bgInput,
            color: soloObligatorios ? "#dc2626" : txtMuted,
          }}>
          {soloObligatorios ? "● Solo obligatorios" : "Todos los campos"}
        </button>
        </>
        )}
        {vePorTipo && (
        <button
          onClick={() => setMostrarTipos(v => !v)}
          style={{
            padding: "6px 12px", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer",
            border: `1px solid ${mostrarTipos ? "#0891b2" : border}`,
            background: mostrarTipos ? (isDark ? "#0a2a33" : "#ecfeff") : bgInput,
            color: mostrarTipos ? "#0891b2" : txtMuted,
          }}>
          {mostrarTipos ? "▾ Tipos inmueble" : "▸ Tipos inmueble"}
        </button>
        )}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: txtMuted }}>{camposFiltrados.length} campo{camposFiltrados.length !== 1 ? "s" : ""}</span>
      </div>

      {/* TABLA */}
      <div style={{ borderRadius: 10, border: `1px solid ${border}`, background: bgCard, overflow: "auto", maxHeight: "calc(100vh - 260px)" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
            <tr style={{ background: bgHead, boxShadow: `0 2px 4px ${isDark ? "rgba(0,0,0,0.5)" : "rgba(0,0,0,0.08)"}` }}>
              <th style={{ padding: "10px 14px", textAlign: "left", fontSize: 10, fontWeight: 800, color: txtMuted, textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: `2px solid ${border}`, minWidth: 200 }}>
                Campo
              </th>
              {veToggle && (
              <th style={{ padding: "10px 14px", textAlign: "center", fontSize: 10, fontWeight: 800, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: `2px solid ${border}`, width: 80 }}>
                Obligat.
              </th>
              )}
              {veResponsable && ROLES.map(r => (
                <th key={r.key} style={{ padding: "10px 14px", textAlign: "center", fontSize: 10, fontWeight: 800, color: r.color, textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: `2px solid ${border}`, width: 80 }}>
                  {r.label}
                </th>
              ))}
              {mostrarTipos && vePorTipo && tiposInmueble.map(ti => (
                <th key={ti.id} style={{
                  padding: "6px 4px", textAlign: "center", fontSize: 9, fontWeight: 700, color: "#0891b2",
                  textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: `2px solid ${border}`,
                  width: 52, minWidth: 52, whiteSpace: "nowrap",
                  writingMode: "vertical-lr", transform: "rotate(180deg)", height: 80,
                }}>
                  {ti.nombre}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from(camposAgrupados.entries()).map(([grupo, camposGrupo]) => {
              const colorGrupo = GRUPO_COLORES[grupo] || "#64748b";
              return [
                // Separador de grupo
                <tr key={`sep-${grupo}`}>
                  <td colSpan={1 + (veToggle ? 1 : 0) + (veResponsable ? ROLES.length : 0) + (mostrarTipos && vePorTipo ? tiposInmueble.length : 0)} style={{
                    padding: "6px 14px",
                    background: isDark ? `${colorGrupo}18` : `${colorGrupo}0d`,
                    borderTop: `1px solid ${border}`,
                    borderBottom: `1px solid ${colorGrupo}45`,
                  }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: colorGrupo, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      ▸ {grupo}
                    </span>
                  </td>
                </tr>,
                // Filas de campos
                ...camposGrupo.map((campo, i) => {
                  const esObligatorio = campo.obligatorio ?? false;
                  const responsable   = campo.responsable ?? [];
                  const bgFila = i % 2 === 0 ? bgCard : (isDark ? "#141820" : "#fafbfc");

                  return (
                    <tr key={campo.id} style={{ background: bgFila, borderBottom: `1px solid ${border}` }}>

                      {/* Nombre + key */}
                      <td style={{ padding: "8px 14px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 3, height: 20, borderRadius: 2, background: esObligatorio ? "#dc2626" : border, flexShrink: 0 }} />
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: txtMain }}>{campo.campo_label}</div>
                            <div style={{ fontSize: 10, color: txtMuted, fontFamily: "monospace" }}>{campo.campo_key}</div>
                          </div>
                        </div>
                      </td>

                      {/* Toggle Obligatorio */}
                      {veToggle && (
                      <td style={{ padding: "8px 14px", textAlign: "center" }}>
                        <button
                          onClick={() => modToggle && toggleObligatorio(campo.id)}
                          disabled={!modToggle}
                          title={!modToggle ? "No tienes permiso para marcar/desmarcar obligatorio" : (esObligatorio ? "Quitar obligatorio" : "Marcar obligatorio")}
                          style={{
                            width: 32, height: 32, borderRadius: 8, border: "none",
                            cursor: modToggle ? "pointer" : "not-allowed", fontSize: 16, transition: "all 0.15s",
                            background: esObligatorio
                              ? (isDark ? "#3b0f0f" : "#fee2e2")
                              : (isDark ? "#1e2535" : "#f1f5f9"),
                            color: esObligatorio ? "#dc2626" : txtMuted,
                            transform: esObligatorio ? "scale(1.1)" : "scale(1)",
                            opacity: modToggle ? 1 : 0.5,
                          }}>
                          {esObligatorio ? "●" : "○"}
                        </button>
                      </td>
                      )}

                      {/* Checkboxes de responsable */}
                      {veResponsable && ROLES.map(r => {
                        const activo = responsable.includes(r.key);
                        return (
                          <td key={r.key} style={{ padding: "8px 14px", textAlign: "center" }}>
                            <button
                              onClick={() => modResponsable && toggleResponsable(campo.id, r.key)}
                              disabled={!modResponsable}
                              title={!modResponsable ? "No tienes permiso para asignar responsable" : (activo ? `Quitar ${r.label}` : `Asignar a ${r.label}`)}
                              style={{
                                width: 32, height: 32, borderRadius: 8, border: "none",
                                cursor: modResponsable ? "pointer" : "not-allowed", fontSize: 14, fontWeight: 800,
                                transition: "all 0.15s",
                                background: activo
                                  ? (isDark ? r.bgDark : r.bg)
                                  : (isDark ? "#1e2535" : "#f1f5f9"),
                                color: activo ? r.color : txtMuted,
                                transform: activo ? "scale(1.1)" : "scale(1)",
                                opacity: modResponsable ? 1 : 0.5,
                              }}>
                              {activo ? "✓" : "—"}
                            </button>
                          </td>
                        );
                      })}

                      {/* Toggles tipo inmueble — solo activos si el campo es obligatorio */}
                      {mostrarTipos && vePorTipo && tiposInmueble.map(ti => {
                        if (!esObligatorio) {
                          return (
                            <td key={ti.id} style={{ padding: "4px 2px", textAlign: "center" }}>
                              <span style={{
                                display: "inline-block", width: 26, height: 26, borderRadius: 6,
                                fontSize: 11, lineHeight: "26px", textAlign: "center",
                                background: isDark ? "#1a1e28" : "#f1f5f9", color: isDark ? "#333" : "#d1d5db",
                              }}>—</span>
                            </td>
                          );
                        }
                        const tiposArr = campo.tipos_inmueble || [];
                        const activo = tiposArr.includes(ti.id);
                        return (
                          <td key={ti.id} style={{ padding: "4px 2px", textAlign: "center" }}>
                            <button
                              onClick={() => modPorTipo && toggleTipoInmueble(campo.id, ti.id)}
                              disabled={!modPorTipo}
                              title={!modPorTipo ? "No tienes permiso para aplicar por tipo de inmueble" : `${ti.nombre}: ${activo ? "obligatorio" : "no aplica"}`}
                              style={{
                                width: 26, height: 26, borderRadius: 6, border: "none",
                                cursor: modPorTipo ? "pointer" : "not-allowed", fontSize: 11, fontWeight: 700,
                                transition: "all 0.15s",
                                background: activo
                                  ? (isDark ? "#0a2a33" : "#cffafe")
                                  : (isDark ? "#1e2535" : "#f1f5f9"),
                                color: activo ? "#0891b2" : txtMuted,
                                opacity: modPorTipo ? 1 : 0.5,
                              }}>
                              {activo ? "✓" : "—"}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ];
            })}
          </tbody>
        </table>

        {camposFiltrados.length === 0 && (
          <div style={{ padding: 32, textAlign: "center", color: txtMuted, fontSize: 13 }}>
            No hay campos que coincidan con los filtros.
          </div>
        )}
      </div>

      {/* PIE */}
      <div style={{ marginTop: 12, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ fontSize: 11, color: txtMuted }}>
          💡 Los campos marcados como obligatorios bloquearán el avance en el Pipeline.
          {mostrarTipos && <span style={{ color: "#0891b2" }}> · Tipos inmueble: marca ✓ en los tipos donde aplique. Sin marcas = aplica a todos.</span>}
        </div>
        <div style={{ flex: 1 }} />
        {cambios && puedeGuardarAlgo && (
          <button onClick={guardar} disabled={guardando} style={{
            padding: "8px 20px", borderRadius: 8, border: "none",
            background: "#f59e0b", color: "#fff", fontSize: 13, fontWeight: 700,
            cursor: guardando ? "not-allowed" : "pointer",
          }}>
            {guardando ? "Guardando..." : "Guardar cambios"}
          </button>
        )}
      </div>
    </div>
  );
}
